import React, { useState, useEffect, useCallback } from 'react';
import { 
  ScrollText, Search, Filter, Download, Calendar, User, 
  FileText, Settings, Shield, AlertTriangle, Loader2, RefreshCw,
  ChevronLeft, ChevronRight, Eye, Clock, Building2, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription as DialogDesc } from '@/components/ui/dialog';
import api from '@/lib/api';

const ACTION_LABELS = {
  'REPORT_CREATED': { label: 'Denúncia Criada', color: 'bg-blue-100 text-blue-700', icon: FileText },
  'REPORT_UPDATED': { label: 'Denúncia Atualizada', color: 'bg-amber-100 text-amber-700', icon: FileText },
  'REPORT_STATUS_CHANGED': { label: 'Estado Alterado', color: 'bg-purple-100 text-purple-700', icon: Activity },
  'REPORT_ASSIGNED': { label: 'Denúncia Atribuída', color: 'bg-green-100 text-green-700', icon: User },
  'USER_LOGIN': { label: 'Login', color: 'bg-slate-100 text-slate-700', icon: User },
  'USER_LOGOUT': { label: 'Logout', color: 'bg-slate-100 text-slate-700', icon: User },
  'USER_CREATED': { label: 'Utilizador Criado', color: 'bg-green-100 text-green-700', icon: User },
  'USER_UPDATED': { label: 'Utilizador Atualizado', color: 'bg-amber-100 text-amber-700', icon: User },
  'SETTINGS_UPDATED': { label: 'Definições Atualizadas', color: 'bg-amber-100 text-amber-700', icon: Settings },
  'MFA_ENABLED': { label: 'MFA Ativado', color: 'bg-green-100 text-green-700', icon: Shield },
  'MFA_DISABLED': { label: 'MFA Desativado', color: 'bg-red-100 text-red-700', icon: Shield },
  'TENANT_CREATED': { label: 'Tenant Criado', color: 'bg-blue-100 text-blue-700', icon: Building2 },
  'TENANT_UPDATED': { label: 'Tenant Atualizado', color: 'bg-amber-100 text-amber-700', icon: Building2 },
  'CONFLICT_DETECTED': { label: 'Conflito Detetado', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  'CONFLICT_RESOLVED': { label: 'Conflito Resolvido', color: 'bg-green-100 text-green-700', icon: AlertTriangle },
  'MESSAGE_SENT': { label: 'Mensagem Enviada', color: 'bg-blue-100 text-blue-700', icon: FileText },
  'WORKFLOW_APPROVED': { label: 'Workflow Aprovado', color: 'bg-green-100 text-green-700', icon: Activity },
  'WORKFLOW_REJECTED': { label: 'Workflow Rejeitado', color: 'bg-red-100 text-red-700', icon: Activity },
  'FILE_UPLOADED': { label: 'Ficheiro Carregado', color: 'bg-blue-100 text-blue-700', icon: FileText },
  'PDF_EXPORTED': { label: 'PDF Exportado', color: 'bg-purple-100 text-purple-700', icon: FileText },
};

const ROLE_LABELS = {
  'super_admin': 'Super Admin',
  'company_admin': 'Administrador',
  'compliance_officer': 'Compliance Officer',
  'triage_manager': 'Gestor Triagem',
  'case_manager': 'Gestor Casos',
  'dpo': 'DPO',
  'anonymous': 'Anónimo'
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    tenant_id: 'all',
    action: 'all',
    user_role: 'all',
    date_from: '',
    date_to: '',
    search: ''
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const limit = 20;
  
  // Detail dialog
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await api.get('/super-admin/tenants');
      setTenants(response.data.tenants || []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (filters.tenant_id && filters.tenant_id !== 'all') params.append('tenant_id', filters.tenant_id);
      if (filters.action && filters.action !== 'all') params.append('action', filters.action);
      if (filters.user_role && filters.user_role !== 'all') params.append('user_role', filters.user_role);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/super-admin/audit-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotalLogs(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / limit));
    } catch (err) {
      setError('Erro ao carregar logs de auditoria');
      setLogs([]);
      setTotalLogs(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/super-admin/audit-logs/stats');
      setStats(response.data);
    } catch (err) {
      // Stats are optional, don't show error
      setStats(null);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      tenant_id: 'all',
      action: 'all',
      user_role: 'all',
      date_from: '',
      date_to: '',
      search: ''
    });
    setPage(1);
  };

  const handleExport = async () => {
    try {
      // In production, this would download a CSV/Excel file
      const csvContent = logs.map(log => 
        `${log.created_at},${log.user_name},${log.action},${log.resource_type},${log.resource_id}`
      ).join('\n');
      
      const blob = new Blob([`Data,Utilizador,Ação,Tipo,ID\n${csvContent}`], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      setError('Erro ao exportar logs');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionInfo = (action) => {
    return ACTION_LABELS[action] || { label: action, color: 'bg-slate-100 text-slate-700', icon: Activity };
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-indigo-700" />
            Auditoria Avançada
          </h1>
          <p className="text-slate-500 mt-1">
            Histórico completo de ações na plataforma
          </p>
        </div>
        <Button 
          onClick={handleExport}
          variant="outline"
          className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-500/50">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <ScrollText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Registos</p>
                  <p className="text-xl font-bold text-slate-900">{stats.total_logs?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Hoje</p>
                  <p className="text-xl font-bold text-slate-900">{stats.logs_today}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Utilizadores Ativos</p>
                  <p className="text-xl font-bold text-slate-900">{stats.unique_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Activity className="w-5 h-5 text-indigo-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ação Mais Frequente</p>
                  <p className="text-sm font-medium text-slate-900">
                    {getActionInfo(stats.most_common_action).label}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-slate-900 flex items-center gap-2 text-base">
            <Filter className="w-4 h-4 text-indigo-700" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Pesquisar por utilizador..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 bg-white border-slate-300 text-slate-900"
                  data-testid="audit-search-input"
                />
              </div>
            </div>

            {/* Tenant Filter */}
            <Select value={filters.tenant_id} onValueChange={(v) => handleFilterChange('tenant_id', v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="audit-tenant-filter">
                <SelectValue placeholder="Todos os Clientes" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                <SelectItem value="all" className="text-slate-900">Todos os Clientes</SelectItem>
                {tenants.map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-slate-900">{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Action Filter */}
            <Select value={filters.action} onValueChange={(v) => handleFilterChange('action', v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="audit-action-filter">
                <SelectValue placeholder="Todas as Ações" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300 max-h-60">
                <SelectItem value="all" className="text-slate-900">Todas as Ações</SelectItem>
                {Object.entries(ACTION_LABELS).map(([key, val]) => (
                  <SelectItem key={key} value={key} className="text-slate-900">{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Role Filter */}
            <Select value={filters.user_role} onValueChange={(v) => handleFilterChange('user_role', v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="audit-role-filter">
                <SelectValue placeholder="Todos os Perfis" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                <SelectItem value="all" className="text-slate-900">Todos os Perfis</SelectItem>
                {Object.entries(ROLE_LABELS).map(([key, val]) => (
                  <SelectItem key={key} value={key} className="text-slate-900">{val}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            <Button 
              variant="outline" 
              onClick={clearFilters}
              className="border-slate-300 text-slate-600 hover:bg-slate-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Limpar
            </Button>
          </div>

          {/* Date Range */}
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Label className="text-slate-500 whitespace-nowrap">De:</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="bg-white border-slate-300 text-slate-900 w-40"
                data-testid="audit-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-slate-500 whitespace-nowrap">Até:</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="bg-white border-slate-300 text-slate-900 w-40"
                data-testid="audit-date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-700" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <ScrollText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum registo encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 text-slate-500 font-medium">Data/Hora</th>
                    <th className="text-left p-4 text-slate-500 font-medium">Utilizador</th>
                    <th className="text-left p-4 text-slate-500 font-medium">Ação</th>
                    <th className="text-left p-4 text-slate-500 font-medium">Recurso</th>
                    <th className="text-left p-4 text-slate-500 font-medium">Cliente</th>
                    <th className="text-right p-4 text-slate-500 font-medium">Detalhes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {logs.map((log) => {
                    const actionInfo = getActionInfo(log.action);
                    const ActionIcon = actionInfo.icon;
                    return (
                      <tr key={log.id} className="hover:bg-slate-100/50">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-500" />
                            <span className="text-slate-900 text-sm">{formatDate(log.created_at)}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="text-slate-900 text-sm">{log.user_name || 'Sistema'}</p>
                            <p className="text-xs text-slate-500">{ROLE_LABELS[log.user_role] || log.user_role}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={`${actionInfo.color} flex items-center gap-1 w-fit`}>
                            <ActionIcon className="w-3 h-3" />
                            {actionInfo.label}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-600 text-sm">
                            {log.resource_type === 'report' ? 'Denúncia' : 
                             log.resource_type === 'user' ? 'Utilizador' : 
                             log.resource_type === 'tenant' ? 'Cliente' : 'Sistema'}
                            {log.resource_id && <span className="text-slate-500"> #{log.resource_id.slice(-6)}</span>}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-500 text-sm">{log.tenant_name || 'Global'}</span>
                        </td>
                        <td className="p-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedLog(log);
                              setShowDetailDialog(true);
                            }}
                            className="text-slate-500 hover:text-slate-900"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                A mostrar {((page - 1) * limit) + 1} - {Math.min(page * limit, totalLogs)} de {totalLogs} registos
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-slate-300 text-slate-600"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-slate-900 px-2">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="border-slate-300 text-slate-600"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-50 border-slate-200 text-slate-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-indigo-700" />
              Detalhes do Registo
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Data/Hora</p>
                  <p className="text-sm text-slate-900">{formatDate(selectedLog.created_at)}</p>
                </div>
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Utilizador</p>
                  <p className="text-sm text-slate-900">{selectedLog.user_name || 'Sistema'}</p>
                </div>
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Perfil</p>
                  <p className="text-sm text-slate-900">{ROLE_LABELS[selectedLog.user_role] || selectedLog.user_role}</p>
                </div>
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">Cliente</p>
                  <p className="text-sm text-slate-900">{selectedLog.tenant_name || 'Global'}</p>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Ação</p>
                <Badge className={getActionInfo(selectedLog.action).color}>
                  {getActionInfo(selectedLog.action).label}
                </Badge>
              </div>

              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Recurso</p>
                <p className="text-sm text-slate-900">
                  {selectedLog.resource_type} - {selectedLog.resource_id}
                </p>
              </div>

              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Detalhes Adicionais</p>
                  <pre className="text-xs text-slate-600 bg-slate-50 p-2 rounded overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.id && (
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-xs text-slate-500">ID do Registo</p>
                  <p className="text-xs text-slate-500 font-mono">{selectedLog.id}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
